if (!customElements.get('nd-wholesale-eligibility')) {
  customElements.define(
    'nd-wholesale-eligibility',
    class WholesaleEligibility extends HTMLElement {
      connectedCallback() {
        this.eligibleVariantIds = new Set(
          String(this.dataset.eligibleVariantIds || '')
            .split(',')
            .filter(Boolean)
        );
        this.hiddenVariantIds = new Set(
          String(this.dataset.hiddenVariantIds || '').split(',').filter(Boolean)
        );
        this.eligibleState = this.querySelector('[data-wholesale-eligible]');
        this.ineligibleState = this.querySelector('[data-wholesale-ineligible]');
        this.priceTable = this.querySelector('[data-wholesale-price-table]');
        this.eligiblePriceNote = this.querySelector('[data-wholesale-price-note-eligible]');
        this.ineligiblePriceNote = this.querySelector('[data-wholesale-price-note-ineligible]');
        this.retailPrice = this.querySelector('[data-wholesale-retail-price]');
        this.tierPrices = [...this.querySelectorAll('[data-wholesale-tier-price]')];
        this.variants = new Map();
        try {
          const variants = JSON.parse(
            this.querySelector('[data-wholesale-variant-data]')?.textContent || '[]'
          );
          this.variants = new Map(variants.map((variant) => [String(variant.id), variant]));
        } catch (error) {
          this.variants = new Map();
        }

        this.applyWholesaleOptionFilter();

        const selectedVariantId = this.closest('product-info')
          ?.querySelector('product-form input[name="id"]')
          ?.value;
        if (selectedVariantId) this.sync(selectedVariantId);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId || '') !== String(this.dataset.sectionId || '')) return;
          this.sync(data?.variant?.id);
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;
      }

      applyWholesaleOptionFilter() {
        if (document.documentElement.dataset.ndB2bView !== 'wholesale') return;

        const eligibleVariants = [...this.variants.values()].filter(
          (variant) => this.eligibleVariantIds.has(String(variant.id)) && variant.available !== false
        );
        if (!eligibleVariants.length) return;

        const productInfo = this.closest('product-info');
        const variantPicker = productInfo?.querySelector('variant-selects');
        if (!variantPicker) return;

        const optionGroups = [...variantPicker.querySelectorAll('.product-form__input')];
        optionGroups.forEach((group, index) => {
          const allowedValues = new Set(
            eligibleVariants.map((variant) => String(variant.options?.[index] || ''))
          );

          const select = group.querySelector('select');
          if (select) {
            const options = [...select.options];
            const availableGroup = document.createElement('optgroup');
            availableGroup.label = 'Available options for Wholesale';
            const unavailableGroup = document.createElement('optgroup');
            unavailableGroup.label = 'Not available for Wholesale';

            options.forEach((option) => {
              const allowed = allowedValues.has(String(option.value));
              option.disabled = !allowed;
              option.toggleAttribute('data-nd-wholesale-unavailable-option', !allowed);
              (allowed ? availableGroup : unavailableGroup).append(option);
            });

            select.append(availableGroup);
            if (unavailableGroup.children.length) select.append(unavailableGroup);
            return;
          }

          const inputs = [...group.querySelectorAll('input[type="radio"]')];
          if (!inputs.length) return;

          const availableGroup = document.createElement('div');
          availableGroup.className = 'nd-wholesale-option-group nd-wholesale-option-group--available';
          availableGroup.innerHTML =
            '<p class="nd-wholesale-option-group__title">Available options for Wholesale</p>';

          const unavailableGroup = document.createElement('div');
          unavailableGroup.className = 'nd-wholesale-option-group nd-wholesale-option-group--unavailable';
          unavailableGroup.innerHTML =
            '<p class="nd-wholesale-option-group__title">Not available for Wholesale</p>' +
            '<p class="nd-wholesale-option-group__note">These options are available in the Retail Store.</p>';

          inputs.forEach((input) => {
            const allowed = allowedValues.has(String(input.value));
            const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            input.disabled = !allowed;
            input.toggleAttribute('data-nd-wholesale-unavailable-option', !allowed);
            label?.toggleAttribute('data-nd-wholesale-unavailable-option', !allowed);
            const destination = allowed ? availableGroup : unavailableGroup;
            destination.append(input);
            if (label) destination.append(label);
          });

          group.append(availableGroup);
          if (unavailableGroup.querySelector('input')) group.append(unavailableGroup);
        });

        const selectedVariantId = productInfo
          ?.querySelector('product-form input[name="id"]')
          ?.value;
        if (this.eligibleVariantIds.has(String(selectedVariantId || ''))) return;

        const firstEligibleVariant = eligibleVariants[0];
        let changedControl;
        optionGroups.forEach((group, index) => {
          const targetValue = String(firstEligibleVariant.options?.[index] || '');
          const select = group.querySelector('select');
          if (select) {
            const matchingOption = [...select.options].find(
              (option) => String(option.value) === targetValue
            );
            if (matchingOption) {
              [...select.options].forEach((option) => option.removeAttribute('selected'));
              matchingOption.selected = true;
              matchingOption.setAttribute('selected', 'selected');
              changedControl = select;
            }
            return;
          }

          const matchingInput = [...group.querySelectorAll('input[type="radio"]')].find(
            (input) => String(input.value) === targetValue
          );
          if (matchingInput) {
            matchingInput.checked = true;
            changedControl = matchingInput;
          }
        });

        changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      sync(variantId) {
        this.hidden = this.hiddenVariantIds.has(String(variantId || ''));
        const eligible = this.eligibleVariantIds.has(String(variantId || ''));
        if (this.eligibleState) this.eligibleState.hidden = !eligible;
        if (this.ineligibleState) this.ineligibleState.hidden = eligible;
        if (this.priceTable) this.priceTable.dataset.wholesalePriceEligible = String(eligible);
        if (this.eligiblePriceNote) this.eligiblePriceNote.hidden = !eligible;
        if (this.ineligiblePriceNote) this.ineligiblePriceNote.hidden = eligible;

        const variant = this.variants.get(String(variantId || ''));
        const retailCents = Number(variant?.price);
        if (!Number.isFinite(retailCents)) return;

        const currency = window.Shopify?.currency?.active || 'USD';
        const formatMoney = (cents) =>
          new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency
          }).format(cents / 100);
        if (this.retailPrice) this.retailPrice.textContent = formatMoney(retailCents);
        this.tierPrices.forEach((cell) => {
          if (!eligible) {
            cell.textContent = 'Not eligible';
            return;
          }
          const percent = Number(cell.dataset.wholesaleTierPrice || 0);
          const discountCents = Math.floor((retailCents * percent) / 100);
          cell.textContent = formatMoney(retailCents - discountCents);
        });
      }
    }
  );
}
